export const startCamera = async (videoRef, setCameraOn) => {
  let stream;

  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error(
        "Camera access is unavailable. Use HTTPS and a supported browser.",
      );
    }

    const isMobile =
      (navigator.userAgentData && navigator.userAgentData.mobile) ||
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

    // Request the rear camera on phones. Use the default webcam on computers.
    const videoConstraints = isMobile
      ? { facingMode: { ideal: "environment" } }
      : true;

    stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio: false,
    });

    // The app renders the <video> element only after cameraOn becomes true.
    // Trigger that render before trying to access videoRef.current.
    if (setCameraOn) {
      setCameraOn(true);
    }

    // Wait briefly for React to render the conditional <video> element.
    for (let attempt = 0; attempt < 10 && !videoRef.current; attempt += 1) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    const video = videoRef.current;
    if (!video) {
      stream.getTracks().forEach((track) => track.stop());
      if (setCameraOn) setCameraOn(false);
      throw new Error("The camera preview element was not rendered.");
    }

    video.srcObject = stream;
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;

    // Wait for the browser to start the preview.
    await video.play();
  } catch (err) {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    console.error("Camera error:", err);
    if (setCameraOn) {
      setCameraOn(false);
    }

    const isMobile =
      (navigator.userAgentData && navigator.userAgentData.mobile) ||
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

    if (!window.isSecureContext) {
      alert(
        "La caméra nécessite une connexion HTTPS. Ouvrez le site avec son adresse https://.",
      );
    } else if (
      err.name === "NotAllowedError" ||
      err.name === "PermissionDeniedError"
    ) {
      alert(
        "Accès à la caméra refusé. Autorisez la caméra dans les paramètres du navigateur puis réessayez.",
      );
    } else if (
      isMobile &&
      (err.name === "OverconstrainedError" || err.name === "NotFoundError")
    ) {
      alert(
        "Caméra introuvable sur ce téléphone. Vérifiez les autorisations et réessayez.",
      );
    } else {
      alert(
        "Impossible d'ouvrir la caméra. Vérifiez les autorisations du navigateur et qu'aucune autre application n'utilise la caméra.",
      );
    }
  }
};

export const stopCamera = (videoRef, setCameraOn) => {
  if (videoRef.current && videoRef.current.srcObject) {
    videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
  }

  if (setCameraOn) {
    setCameraOn(false); // ✅ safe
  }
};

export const capturePhoto = async ({
  videoRef,
  canvasRef,
  onCapture, // 🔥 ONE callback for both App & Admin
  setCameraOn,
}) => {
  const video = videoRef.current;
  const canvas = canvasRef.current;

  if (!video || !canvas) return;

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(video, 0, 0);

  const dataUrl = canvas.toDataURL("image/jpeg");

  const res = await fetch(dataUrl);
  const blob = await res.blob();

  const file = new File([blob], "webcam.jpg", {
    type: "image/jpeg",
  });

  // ✅ Let each component decide what to do
  if (onCapture) {
    onCapture({ file, preview: dataUrl });
  }

  stopCamera(videoRef, setCameraOn);
};
